import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, MenuItem, FormControl, InputLabel, Select, TextField, Box, Alert } from '@mui/material';

export default function ChangeHotelStatusModal({ open, onClose, onSave, hotel }) {
    const [status, setStatus] = useState('Active');
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [currentStatus, setCurrentStatus] = useState('');

    useEffect(() => {
        if (hotel) {
            // Capitalize first letter to match dropdown value options
            const current = hotel.status ? hotel.status.charAt(0).toUpperCase() + hotel.status.slice(1).toLowerCase() : 'Setup';
            setCurrentStatus(current);
            const options = getAvailableStatuses(current);
            setStatus(options.length > 0 ? options[0] : '');
            setReason('');
            setError('');
        }
    }, [hotel, open]);

    const getAvailableStatuses = (current) => {
        switch (current) {
            case 'Setup': return ['Active', 'Inactive', 'Suspended'];
            case 'Active': return ['Inactive', 'Suspended'];
            case 'Inactive': return ['Active', 'Suspended'];
            case 'Suspended': return ['Active', 'Inactive'];
            case 'Archived': return [];
            default: return [];
        }
    };

    const availableStatuses = getAvailableStatuses(currentStatus);

    const handleSubmit = async () => {
        if (!reason.trim()) {
            setError('Reason for change is required');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            await onSave({
                id: hotel.id,
                tenantId: hotel.tenantId,
                status,
                reason: reason.trim()
            });
            onClose();
        } catch (error) {
            console.error("Failed to change status in modal:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : onClose}
            PaperProps={{ sx: { borderRadius: 3, padding: 1, minWidth: 400 } }}
        >
            <DialogTitle fontWeight="bold">Change Hotel Status</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            label="Status"
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={isSubmitting || availableStatuses.length === 0}
                        >
                            {availableStatuses.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Reason for Change"
                        placeholder="e.g. Renovation, Onboarding completed, Maintenance"
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (e.target.value.trim()) setError('');
                        }}
                        disabled={isSubmitting}
                        size="small"
                        multiline
                        rows={3}
                        required
                        error={!!error}
                        helperText={error}
                    />

                    {['Inactive', 'Suspended'].includes(status) && (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            <b>Warning:</b> Changing the status to {status} will automatically and instantly trigger a global Stop-Sell (0 availability) payload to all connected OTA channels, bypassing standard sync intervals.
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 16px 24px' }}>
                <Button onClick={onClose} color="primary" variant="outlined" sx={{ borderRadius: 2 }} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} color="primary" variant="contained" sx={{ borderRadius: 2 }} disabled={isSubmitting || availableStatuses.length === 0}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
